import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { MapPin, Briefcase, CheckCircle2, Calendar, ArrowLeft, Flag } from "lucide-react";
import { api, fileUrl, formatDate } from "@/lib/api";
import { StarRating, RatingBreakdown, ReputationBadge, ReputationProgress } from "@/components/StarRating";
import { Loading, EmptyState } from "@/components/EmptyState";
import { ReportModal } from "@/components/ReportModal";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";

export default function PublicProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reportOpen, setReportOpen] = useState(false);
  const [filterStar, setFilterStar] = useState(0);

  useEffect(() => { api.get(`/users/${id}`).then(({ data }) => setData(data)).finally(() => setLoading(false)); }, [id]);

  if (loading) return <Loading />;
  if (!data) return <EmptyState title="Pengguna tidak ditemukan" />;
  const u = data.user;

  return (
    <div className="max-w-2xl mx-auto" data-testid="public-profile">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-slate-500 mb-4 hover:text-slate-800"><ArrowLeft size={18} /> Kembali</button>
      <div className="bg-white rounded-2xl border border-slate-200 p-6 flex flex-col items-center text-center">
        <Avatar className="w-24 h-24 border-2 border-slate-100">
          <AvatarImage src={fileUrl(u.photo_url)} />
          <AvatarFallback className="bg-blue-100 text-blue-700 text-2xl font-bold">{u.name?.[0]}</AvatarFallback>
        </Avatar>
        <h1 className="text-2xl font-extrabold mt-4 flex items-center gap-2">{u.name}{u.reputation && <ReputationBadge level={u.reputation} />}</h1>
        <p className="text-slate-500 flex items-center gap-1 mt-1"><MapPin size={15} className="text-blue-600" /> {u.city || "-"}</p>
        <div className="mt-2"><StarRating value={u.rating_average} count={u.rating_count} /></div>
        <div className="mt-1.5"><ReputationProgress completed={u.completed_jobs} /></div>
        {data.rating_breakdown && u.rating_count > 0 && (
          <div className="w-full mt-5 pt-5 border-t border-slate-100">
            <RatingBreakdown breakdown={data.rating_breakdown} average={u.rating_average} count={u.rating_count} />
          </div>
        )}
        <div className="grid grid-cols-3 gap-3 mt-6 w-full">
          <Stat icon={Briefcase} value={u.jobs_posted} label="Diposting" />
          <Stat icon={CheckCircle2} value={u.completed_jobs} label="Selesai" />
          <Stat icon={Calendar} value={formatDate(u.created_at)} label="Bergabung" small />
        </div>
        {u.bio && <p className="text-slate-600 mt-5">{u.bio}</p>}
        {u.skills?.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-4 justify-center">{u.skills.map((s) => <span key={s} className="bg-slate-100 text-slate-700 px-3 py-1 rounded-full text-sm">{s}</span>)}</div>
        )}
        <Button data-testid="report-user-btn" variant="ghost" size="sm" onClick={() => setReportOpen(true)} className="mt-4 text-slate-400 hover:text-rose-600"><Flag size={15} className="mr-1" /> Laporkan</Button>
      </div>

      {data.reviews?.length > 0 && (
        <div className="mt-6" data-testid="reviews-section">
          <h2 className="font-bold text-slate-900 mb-3">Ulasan ({data.reviews.length})</h2>
          <div className="flex flex-wrap gap-2 mb-4">
            <FilterChip active={filterStar === 0} onClick={() => setFilterStar(0)} label={`Semua (${data.reviews.length})`} testId="filter-star-all" />
            {[5, 4, 3, 2, 1].map((s) => (
              <FilterChip key={s} active={filterStar === s} onClick={() => setFilterStar(s)} label={`${s}★ (${data.rating_breakdown?.[s] || 0})`} testId={`filter-star-${s}`} />
            ))}
          </div>
          <div className="space-y-3">
            {data.reviews.filter((r) => filterStar === 0 || r.rating === filterStar).map((r) => (
              <div key={r.id} className="bg-white rounded-xl border border-slate-200 p-4">
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-slate-800 text-sm">{r.reviewer?.name}</span>
                  <StarRating value={r.rating} showCount={false} size={13} />
                </div>
                {r.comment && <p className="text-slate-600 mt-2 text-sm">{r.comment}</p>}
              </div>
            ))}
            {data.reviews.filter((r) => filterStar === 0 || r.rating === filterStar).length === 0 && (
              <p className="text-slate-400 text-sm text-center py-6">Belum ada ulasan bintang {filterStar}.</p>
            )}
          </div>
        </div>
      )}
      <ReportModal open={reportOpen} onOpenChange={setReportOpen} targetUserId={u.id} />
    </div>
  );
}

const Stat = ({ icon: Icon, value, label, small }) => (
  <div className="bg-slate-50 rounded-xl p-3 text-center">
    <Icon size={16} className="text-blue-600 mx-auto mb-1" />
    <p className={`font-bold text-slate-900 ${small ? "text-xs" : "text-lg"}`}>{value}</p>
    <p className="text-xs text-slate-400">{label}</p>
  </div>
);

const FilterChip = ({ active, onClick, label, testId }) => (
  <button data-testid={testId} onClick={onClick}
    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${active ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
    {label}
  </button>
);
